import { FormEvent, useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Textarea } from "@/components/ui/Textarea";
import { useApi } from "@/lib/api";
import { LineItem } from "@/types/billing";
import { InhabitantsResponse } from "@/types/inhabitants";

export interface LineItemFormData {
  description: string;
  amount: string;
  type: string;
  usage?: string;
  cost_per_flat?: string;
  cost_per_person?: string;
}

interface Props {
  open: boolean;
  initial?: LineItem;
  agreementId: string | null;
  location: string | null;
  onClose: () => void;
  onSubmit: (data: LineItemFormData) => Promise<void>;
}

function toStr(v: string | null | undefined): string {
  return v ?? "";
}

export function AddLineItemModal({ open, initial, agreementId, location, onClose, onSubmit }: Props) {
  const editMode = !!initial;

  const [description, setDescription] = useState("");
  const [type, setType] = useState("");
  const [usage, setUsage] = useState("");
  const [costPerFlat, setCostPerFlat] = useState("");
  const [costPerPerson, setCostPerPerson] = useState("");
  const [amount, setAmount] = useState("");
  const [amountTouched, setAmountTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { data: inhabitantsResp } = useApi<InhabitantsResponse>(
    open && editMode ? "/api/inhabitants?show_all=true" : null
  );
  // This tenant's own household size — drives the na_osobę → do_zapłaty proposal
  // (each tenant pays cost_per_person × their own headcount).
  const tenant = inhabitantsResp?.inhabitants.find((i) => i.agreementId === agreementId);
  // Total residents across every active tenant at the same location — the
  // quick-split buttons reproduce the backend's per-person rate, which divides
  // the whole-flat cost by everyone sharing that location, not just this room.
  const locationTenants = inhabitantsResp?.active_inhabitants.filter((i) => i.location === location) ?? [];
  const totalPersonsAtLocation = locationTenants.reduce((sum, i) => sum + (i.noOfInhabitants > 0 ? i.noOfInhabitants : 1), 0);
  const maxPersons = totalPersonsAtLocation > 0 ? totalPersonsAtLocation : 3;

  useEffect(() => {
    if (!open) return;
    setDescription(initial?.description ?? "");
    setType(initial?.type ?? "");
    setUsage(toStr(initial?.usage));
    setCostPerFlat(toStr(initial?.cost_per_flat));
    setCostPerPerson(toStr(initial?.cost_per_person));
    setAmount(initial?.amount ?? "");
    setAmountTouched(false);
  }, [open, initial]);

  function proposeAmountFrom(perPersonValue: string) {
    if (amountTouched) return;
    const n = Number(perPersonValue);
    if (!perPersonValue || Number.isNaN(n)) return;
    const persons = tenant?.noOfInhabitants && tenant.noOfInhabitants > 0 ? tenant.noOfInhabitants : 1;
    setAmount((n * persons).toFixed(2));
  }

  function handleCostPerPersonChange(value: string) {
    setCostPerPerson(value);
    proposeAmountFrom(value);
  }

  function quickSplit(n: number) {
    const flat = Number(costPerFlat);
    if (!costPerFlat || Number.isNaN(flat)) return;
    const perPerson = (flat / n).toFixed(2);
    setCostPerPerson(perPerson);
    proposeAmountFrom(perPerson);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data: LineItemFormData = editMode
        ? { description, amount, type, usage, cost_per_flat: costPerFlat, cost_per_person: costPerPerson }
        : { description, amount, type };
      await onSubmit(data);
      onClose();
    } finally {
      setSubmitting(false);
    }
  }

  const splitButtons = Array.from({ length: Math.min(maxPersons, 3) }, (_, i) => i + 1);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editMode ? "Edytuj pozycję" : "Dodaj pozycję"}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Anuluj</Button>
          <Button onClick={submit} loading={submitting} disabled={!description || !amount}>
            {editMode ? "Zapisz" : "Dodaj"}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-3">
        <Input id="li-type" label="Typ" placeholder="np. naprawa" value={type} onChange={(e) => setType(e.target.value)} />
        <Textarea id="li-desc" label="Opis" value={description} onChange={(e) => setDescription(e.target.value)} />

        {editMode ? (
          <>
            <Input id="li-usage" label="Zużycie" inputMode="decimal" value={usage} onChange={(e) => setUsage(e.target.value)} />
            <Input id="li-cpf" label="Na mieszkanie (PLN)" inputMode="decimal" value={costPerFlat} onChange={(e) => setCostPerFlat(e.target.value)} />

            <div className="flex flex-col gap-1">
              <Input
                id="li-cpp"
                label="Na osobę (PLN)"
                inputMode="decimal"
                value={costPerPerson}
                onChange={(e) => handleCostPerPersonChange(e.target.value)}
              />
              {!!costPerFlat && (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-text-subtle">Podziel na mieszkanie przez:</span>
                  {splitButtons.map((n) => (
                    <Button key={n} type="button" size="sm" variant="secondary" onClick={() => quickSplit(n)}>
                      ÷{n}
                    </Button>
                  ))}
                </div>
              )}
            </div>

            <Input
              id="li-amt"
              label="Do zapłaty (PLN)"
              inputMode="decimal"
              value={amount}
              onChange={(e) => { setAmount(e.target.value); setAmountTouched(true); }}
            />
          </>
        ) : (
          <Input id="li-amt" label="Kwota (PLN)" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
        )}
      </form>
    </Modal>
  );
}
